/**
 * The tenant's own mail accounts.
 *
 * This module replaces `admin.ts`, which asked an ordinary person to paste a
 * token that administers every tenant on the server and then named their tenant
 * in the URL. Both halves were wrong. The daemon now serves `/accounts` to the
 * ordinary session, and takes the tenant from that session rather than from a
 * path segment -- so there is no slug to send here, and no admin credential in
 * this origin at all. Operator work moved to its own panel on its own port,
 * which is the only way an origin boundary can keep the two apart.
 *
 * # Why the cookie is the only credential this file knows about
 *
 * The Google flow is a top-level browser navigation that follows a redirect to
 * Google and back. A navigation carries cookies and cannot carry an
 * `Authorization` header, so half of this surface is cookie-authenticated
 * whether or not the other half wants to be. Sending a bearer token on the
 * fetches and a cookie on the navigation would mean an account added in one
 * identity and listed in another; one credential for the whole surface removes
 * the question.
 *
 * # What a response is allowed to contain
 *
 * Never a password and never a refresh token -- the server is built so that it
 * cannot serve one, and `settings` is re-emitted field by field there rather
 * than passed through. That guarantee is mirrored here by typing `settings` as
 * the three fields that exist instead of as a bag: a UI that iterated the
 * object would render a future field nobody had thought about, which is the
 * exact failure the server went to the trouble of preventing.
 *
 * # `lastRun` is snake_case and the rest is not
 *
 * Deliberate on the server: `lastRun` is a store row re-emitted verbatim, and
 * renaming its fields in transit would make the daemon's own logs and this
 * client disagree about what to call them. Mirrored rather than papered over,
 * so a reader comparing the two sees the same words.
 */

// The transport seam. `@transport` resolves at build time to the browser
// implementation or the desktop one; nothing below can tell which. Imported
// under the name `fetch` so the call sites are untouched and this import is
// the whole of the difference. See `lib/net.types.ts`.
import { authHeaders } from "./settings";
import {
  onExternalAuthReturn,
  request as fetch,
  startExternalAuth,
} from "@transport";
import type { ExternalAuthReturn } from "./net.types";
import type { AccountColour } from "./palette";

/** A row from `sync_runs`, exactly as the daemon serialises it. */
export interface SyncRun {
  id: number;
  account_id: number;
  trigger: string;
  started_at: number;
  /** Null while the run is in flight. */
  finished_at: number | null;
  outcome: "ok" | "error" | "interrupted" | null;
  folders: number;
  created: number;
  updated: number;
  destroyed: number;
  cursor_resets: number;
  error: string | null;
}

/**
 * Whether this account can sign in, as far as is knowable without connecting.
 *
 * `needs_reauth` is the one that matters. It means Google revoked the grant --
 * a password change, ninety days of inactivity, a security review -- and no
 * amount of retrying will fix it. It is the most common way a Gmail account
 * stops delivering mail, and it does so silently, so it gets the loudest
 * treatment the screen has.
 */
export type CredentialState = "ok" | "needs_reauth" | "no_credential";

/**
 * The provider's own configuration, named field by field.
 *
 * Not `Record<string, unknown>`. See the module comment: the server's promise
 * is that nothing reaches a client until a person writes a line to expose it,
 * and a client that spread this object into a table would break that promise
 * from the other end.
 */
export interface AccountSettings {
  /** imap */
  host?: string;
  port?: number;
  tls?: boolean;
  /**
   * Where this account sends, or null when it cannot send at all.
   *
   * Null is the state every IMAP account starts in and the state both of this
   * server's accounts are in today: reading and sending are separate services
   * on separate hosts, and having one is no evidence of the other. It is what
   * `EmailSubmission/set` refuses on, so it is also the reason the composer
   * says a message cannot be sent before the first keystroke rather than
   * afterwards.
   *
   * Inside `settings` because it is the provider's own configuration, and
   * named field by field for the reason the module comment gives. It holds no
   * credential and cannot: submission signs in with this account's IMAP login.
   */
  submission?: Submission | null;
  /**
   * Who files this account's copy in Sent. Present for imap and gmail.
   *
   * Beside `submission` rather than inside it, and the daemon puts it there on
   * purpose: `submission` is the one field on this API where absent and
   * explicit null already mean different things, and this outlives the
   * submission host anyway -- clearing the host and typing it again is not
   * evidence that the server changed how it behaves.
   */
  sentCopy?: SentCopy;
  /**
   * Which folder plays each role, where somebody has said rather than guessed.
   *
   * All five keys are always present and `null` is the ordinary value: it means
   * nobody has answered and the daemon's own detection decides, which is right
   * for most accounts and is where every account starts.
   *
   * Optional as a whole only because a daemon older than this feature does not
   * send the object at all, and an absent map has to read as five nulls rather
   * than as a screen with nothing on it.
   */
  folderRoles?: FolderRoles;
  /** maildir, which a tenant can see but not create or edit */
  path?: string;
}

/**
 * The roles a folder can be pinned to, and deliberately not `inbox`.
 *
 * INBOX is the one folder IMAP names in the protocol rather than guesses at, so
 * there is nothing to correct and the daemon has no key for it. Offering one
 * would be a control that can only ever be refused.
 */
export type FolderRole = "archive" | "sent" | "drafts" | "trash" | "junk";

/**
 * A JMAP mailbox id per role, or null where detection is left to decide.
 *
 * # Why this exists at all
 *
 * The daemon reads SPECIAL-USE attributes and, where a server publishes none,
 * falls back to matching English folder names. An account whose folders are
 * "Enviados" and "Papelera" therefore gets no roles whatsoever: no Sent, no
 * Trash, no Archive, and the Archive button has nowhere to file. The other
 * failure is the opposite one -- two candidates and the wrong winner. A Gmail
 * account here had a user folder called "Sent" beside `[Gmail]/Sent Mail`, and
 * the unified Sent view showed two messages out of 495.
 *
 * Detection stays; this is what stops it being the final word.
 *
 * The values are the same mailbox ids the client already holds from
 * `Mailbox/get`, so a folder chosen here is a folder the reader can see in the
 * sidebar rather than a name they have to spell the way the server does.
 */
export type FolderRoles = Record<FolderRole, string | null>;

/** The five keys, in the order a mail client lists them. */
export const FOLDER_ROLES: FolderRole[] = [
  "sent",
  "archive",
  "drafts",
  "trash",
  "junk",
];

/** Five nulls: what an account whose daemon predates this feature is saying. */
export function folderRolesOf(settings: AccountSettings): FolderRoles {
  const stored = settings.folderRoles;
  return {
    sent: stored?.sent ?? null,
    archive: stored?.archive ?? null,
    drafts: stored?.drafts ?? null,
    trash: stored?.trash ?? null,
    junk: stored?.junk ?? null,
  };
}

/**
 * Whether the account's own server already put a copy in Sent.
 *
 * Three values and not a boolean, because the honest answer for an account
 * nobody has looked at is "not known", and both ways of collapsing that are
 * mail bugs rather than cosmetic ones. `provider` is that third state: the
 * daemon falls back to what the provider declares, which is right for most
 * accounts and is what every account already on this server is set to.
 *
 * The other two are answers somebody read off their own Sent folder after
 * sending a message through the account. `upstream` means the server filed one,
 * so HydraMail must not add a second; `local` means nobody filed one, so
 * HydraMail must.
 */
export type SentCopy = "provider" | "upstream" | "local";

/**
 * How this account reaches its submission server.
 *
 * `plaintext` is representable because an operator can set it at the CLI, and a
 * client that could not render an account someone else configured would show a
 * blank where a setting is. It cannot be *chosen* here: the daemon refuses it
 * over HTTP, exactly as it refuses cleartext IMAP.
 */
export type SubmissionSecurity = "implicit" | "starttls" | "plaintext";

export interface Submission {
  host: string;
  port: number;
  security: SubmissionSecurity;
}

export interface AccountView {
  id: number;
  name: string;
  /** `"imap"`, `"gmail"` or `"maildir"`. */
  provider: string;
  /**
   * A palette token, never a hex string. See `lib/palette.ts` for why.
   *
   * Typed as `string` rather than as `Colour` on purpose: this is what a server
   * sent, and a server one version ahead of this client may know a colour this
   * one does not. `colourVar` falls back rather than rendering `undefined`.
   */
  colour: string;
  createdAt: number;
  settings: AccountSettings;
  /** The address this account signs in as. Not a secret. */
  login: string | null;
  /** How it authenticates, or null when nothing is stored. */
  credential: "password" | "oauth2" | null;
  auth: CredentialState;
  lastRun: SyncRun | null;
  consecutiveFailures: number;
  messages: number;
  mailboxes: number;
  syncing: boolean;
}

/**
 * The stable vocabulary a failed connection is classified into.
 *
 * Switched on to decide what to offer next -- reopen the password field, offer
 * a reconnect, or say the host is unreachable and change nothing. The prose in
 * `message` is what a person reads; this is what the client reads.
 */
export type TestProblem =
  | "credentials"
  | "reauth"
  | "unreachable"
  | "tls"
  | "config"
  | "no_credential"
  | "unknown"
  // An account has two halves that fail separately, and a client that offered
  // "check the server address" would have to know which address. Everything
  // below is about where the account *sends*.
  | "submission_unreachable"
  | "submission_security"
  | "submission_credentials"
  | "submission_tls"
  | "submission_config";

export interface TestResult {
  ok: boolean;
  /** Absent when `ok`. */
  problem?: TestProblem;
  /** Always present, and always a sentence. */
  message: string;
}

/** What `POST /accounts` takes. Only IMAP: Gmail is authorised, Maildir is not offered. */
export interface NewImapAccount {
  name: string;
  host: string;
  port: number;
  user: string;
  password: string;
  /**
   * Where the new account will send, when it is to send at all.
   *
   * In the same request as the login rather than in a `PATCH` afterwards,
   * because two requests is two chances to end up with the first one applied
   * and not the second -- which is precisely the account that receives and
   * cannot send, the state the whole submission feature exists to make visible.
   * The daemon proves the IMAP login and then this, and stores neither if
   * either is refused.
   *
   * Omitted for an account that only receives. That is a legitimate thing to
   * own and not a half-finished setup.
   */
  submission?: SubmissionPatch;
}

/**
 * Every field optional, and absent means "leave it alone".
 *
 * Not a whole-account replacement, because a form that PUT the whole account
 * would have to send the password back, which means it would have to have been
 * given the password.
 */
export interface AccountPatch {
  name?: string;
  host?: string;
  port?: number;
  user?: string;
  password?: string;
  /**
   * One of `palette.COLOURS`, or a custom colour as `#rrggbb`.
   *
   * The daemon answers 400 naming both forms for anything else, rather than
   * quietly storing a default -- which would leave this client showing a colour
   * the server does not have. It also lowercases what it stores and returns
   * that, so the answer is what to paint from; see `palette.ts` for why a custom
   * colour is one value here and a pair on screen.
   */
  colour?: AccountColour;
  /**
   * Where this account sends -- and `null`, explicitly, to stop it sending.
   *
   * The one field here where absent and null differ, and they have to: absent
   * means "leave sending as it is", which is what every other patch on this
   * screen wants, and null is the only way to turn it off again. `undefined`
   * disappears in `JSON.stringify`, so sending null is a deliberate act at both
   * ends.
   *
   * No password. Submission reuses this account's IMAP credential by design;
   * the daemon has no field for a second one and this client must never grow a
   * box that looks like one.
   */
  submission?: SubmissionPatch | null;
  /**
   * Who files this account's copy in Sent.
   *
   * A plain optional, unlike `submission`: there is no "off" to express, since
   * `"provider"` is itself one of the three values and is how a choice is
   * withdrawn. Absent means leave it alone, as everywhere else here.
   *
   * The daemon stores this without dialling anything, which is the one place
   * this API does not verify before it writes -- and it cannot, because the
   * evidence is in a Sent folder rather than on a socket. An unknown value is
   * still refused with a sentence naming the three, so a client one version
   * behind gets an error rather than a silent default.
   */
  sentCopy?: SentCopy;
  /**
   * Which folder plays which role -- a *partial* map, unlike the one on
   * `settings`.
   *
   * Only the keys sent here change. An explicit `null` clears an override back
   * to detection, which is why the values are `string | null` rather than
   * `string`, and omitting a key changes nothing about it.
   *
   * Partial rather than whole on purpose, and the client has to keep it that
   * way: sending all five every time somebody corrects one would pin the other
   * four to whatever detection happened to say this morning, and the reader
   * would never see it happen. From then on a provider that starts publishing
   * SPECIAL-USE, or a folder that gets renamed, would be overruled by a choice
   * nobody made.
   *
   * Refused with the same `problem`-carrying body as everything else here when
   * a folder belongs to another account, or when two roles would land on one
   * folder. Those sentences are the whole help text for this control, so they
   * are shown as they arrive rather than replaced with a generic failure.
   */
  folderRoles?: Partial<FolderRoles>;
}

/** What a client may say about a submission server. Never a password. */
export interface SubmissionPatch {
  host: string;
  /** Defaults to the conventional port for `security`: 465 or 587. */
  port?: number;
  /** Omitted means implicit TLS, which is what RFC 8314 asks for. */
  security?: "implicit" | "starttls";
}

/**
 * A submission host worth offering, from where an account reads and who it
 * reads as.
 *
 * Takes the two fields rather than an `AccountView` because the add form has no
 * account yet -- it has two boxes somebody is still typing into, and the guess
 * has to follow them keystroke by keystroke or it is a guess about a host that
 * was on screen ten seconds ago.
 *
 * Almost nobody knows theirs, and it is almost always the IMAP host with the
 * first label swapped -- `imap.fastmail.com` and `smtp.fastmail.com`, over and
 * over -- or a single host that serves both, which is what a small server run by
 * one person looks like. When neither applies, the login's own domain is the
 * next best guess. All three are wrong sometimes, which is why the value lands
 * in an editable field beside a Test button rather than being saved.
 *
 * Deliberately local, and deliberately not Mozilla's ISPDB, a `_submission._tcp`
 * SRV lookup, or RFC 6186. Each would guess better; each would also mean this
 * daemon telling a third party which provider its user has an account with, on
 * a box whose whole premise is that it does not do that. A guess someone can
 * correct in one field is a complete answer to the question asked.
 */
export function guessSubmissionHost(
  host: string | null,
  login: string | null,
): string {
  const imap = (host ?? "").trim().toLowerCase();
  // `imap.` and `imaps-`, and the same for the `-mail` spellings some providers
  // use; the separator is kept so `imap-mail.outlook.com` stays well formed.
  if (/^imaps?[.-]/.test(imap)) return imap.replace(/^imaps?([.-])/, "smtp$1");
  // One host doing both jobs. Overwhelmingly a server somebody runs themselves,
  // where inventing an `smtp.` label would point at nothing.
  if (/^(mail|post|mx)\./.test(imap)) return imap;

  const domain = (login ?? "").split("@")[1]?.trim().toLowerCase();
  if (domain) return `smtp.${domain}`;
  return imap;
}

export class AccountsError extends Error {
  readonly status: number;
  /**
   * The same vocabulary a test result uses, when the daemon sent one.
   *
   * A refusal from `POST /accounts` can be about either of the two servers it
   * dialled, and the offer to make next differs: retype the password, correct
   * the submission port, or add the account without sending for now. Only
   * present on the refusals that went and found something out; a name that is
   * already taken has nothing to classify.
   */
  readonly problem: TestProblem | undefined;
  constructor(message: string, status: number, problem?: TestProblem) {
    super(message);
    this.name = "AccountsError";
    this.status = status;
    this.problem = problem;
  }
}

/** Whether a refusal was about where the account sends rather than where it reads. */
export function isSubmissionProblem(error: unknown): boolean {
  return (
    error instanceof AccountsError &&
    (error.problem?.startsWith("submission_") ?? false)
  );
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: "same-origin",
    // On every request including the bodyless ones. The daemon refuses a
    // cookie-authenticated write that is not `application/json`, and that
    // refusal is what stops a cross-site form from forging one -- a form can
    // send three content types and this is not among them.
    // `authHeaders` rather than a bare content-type: it sends the same
    // content-type and adds the bearer when this build has one. A browser has
    // none and is authenticated by its cookie; a packaged build has no cookie,
    // and this line is the whole of what makes the request authenticated at
    // all. See `lib/settings.ts` for what leaving it out cost.
    headers: authHeaders(),
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });

  if (!res.ok) {
    let detail = res.statusText;
    let problem: TestProblem | undefined;
    try {
      const body = await res.json();
      detail = body.error ?? body.detail ?? detail;
      // Absent on most refusals, and absent is not a value: only the ones that
      // dialled a server have something to classify.
      if (typeof body.problem === "string")
        problem = body.problem as TestProblem;
    } catch {
      // A proxy error page is HTML; the status line is all there is.
    }
    throw new AccountsError(detail, res.status, problem);
  }
  // Read the body before deciding it is JSON. Two routes here answer with no
  // body at all -- 204 from a delete, 202 from a sync that was accepted rather
  // than performed -- and `res.json()` on an empty body throws a parse error
  // that reaches the screen as "Unexpected end of JSON input", which describes
  // the client's mistake rather than anything that happened.
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

export function list(): Promise<AccountView[]> {
  return request<AccountView[]>("GET", "/accounts");
}

export function get(id: number): Promise<AccountView> {
  return request<AccountView>("GET", `/accounts/${id}`);
}

/**
 * Add an IMAP account.
 *
 * The daemon connects before it stores anything, and answers 400 with the
 * diagnosis if the login fails -- so this call *is* the test, and there is no
 * window in which a half-working account exists. TLS is not a parameter: the
 * daemon fixes it on, and cleartext IMAP stays an operator decision made at a
 * CLI with a flag named `--insecure`.
 *
 * With a `submission` it dials both servers, in that order, and stores nothing
 * unless both answered. A 400 carrying a `submission_` problem therefore means
 * the login was fine and the sending details were not -- the one case worth
 * telling apart here, because the account is worth adding without them.
 */
export function create(account: NewImapAccount): Promise<AccountView> {
  return request<AccountView>("POST", "/accounts", {
    provider: "imap",
    ...account,
  });
}

export function update(id: number, patch: AccountPatch): Promise<AccountView> {
  return request<AccountView>("PATCH", `/accounts/${id}`, patch);
}

export function remove(id: number): Promise<void> {
  return request<void>("DELETE", `/accounts/${id}`);
}

/**
 * Connect, and report what happened.
 *
 * A wrong password is a 200 carrying `ok: false`, not a 4xx: the request
 * succeeded, and what failed is the thing it went and checked. Callers render
 * `message` and branch on `problem`.
 *
 * With no argument it tests what is stored -- both halves, when the account has
 * a submission host, because "the mail arrives" was never the whole question.
 * With a `candidate` it tests *that* submission server instead and stores
 * nothing, which is how somebody finds out whether they guessed the port right
 * without first saving the guess.
 */
export function test(
  id: number,
  candidate?: SubmissionPatch,
): Promise<TestResult> {
  return request<TestResult>(
    "POST",
    `/accounts/${id}/test`,
    candidate ? { submission: candidate } : undefined,
  );
}

/** What the daemon says about a sync it was asked to run now. */
export type SyncOutcome = "queued" | "already-running" | "unavailable";

/**
 * Ask for a sync now.
 *
 * 202, not 200: the sync is accepted rather than performed, and its result
 * arrives later in this account's `lastRun`. 409 means one is already running,
 * which is not an error worth colouring red -- it is the answer to the question
 * the button asked.
 */
export async function syncNow(id: number): Promise<SyncOutcome> {
  try {
    await request<void>("POST", `/accounts/${id}/sync`);
    return "queued";
  } catch (e) {
    if (e instanceof AccountsError && e.status === 409)
      return "already-running";
    if (e instanceof AccountsError && (e.status === 503 || e.status === 501))
      return "unavailable";
    throw e;
  }
}

// ---- the OAuth flows ---------------------------------------------------------

/**
 * The path segment each provider's flow lives under.
 *
 * Not the provider kind, and the difference is not cosmetic. Google's flow is
 * at `/accounts/oauth/google/...` because Google issues the token, while the
 * provider kind is `gmail` because that is the mail service — and the daemon's
 * redirect URI is registered by hand in the Google console and matched there as
 * an exact string. Microsoft's two names happen to coincide, which is exactly
 * why the Gmail case needs writing down.
 */
const OAUTH_SLUG: Record<string, string> = {
  gmail: "google",
  microsoft: "microsoft",
};

/** What to call each provider in a sentence a person reads. */
export const PROVIDER_LABEL: Record<string, string> = {
  gmail: "Google",
  microsoft: "Microsoft",
  imap: "IMAP",
  maildir: "Maildir",
};

/**
 * Begin an authorisation for a provider kind (`"gmail"`, `"microsoft"`).
 *
 * Where it happens is the transport's business and differs sharply between
 * builds: a browser navigates to the daemon and comes back as a page load, while
 * a desktop app has to open the *system* browser, because both issuers refuse
 * OAuth inside an embedded webview as anti-phishing policy. What is shared is
 * the URL, which is what this function is for.
 *
 * `name` is only for a second account with the same provider: account names are
 * unique per tenant and are the key the credential is filed under, so the second
 * one needs a name of its own. The daemon defaults it to the provider kind.
 */
export function startOAuth(kind: string, name?: string): void {
  const slug = OAUTH_SLUG[kind];
  if (!slug) return;
  const query = name ? `?name=${encodeURIComponent(name)}` : "";
  startExternalAuth(`/accounts/oauth/${slug}/start${query}`);
}

/**
 * Begin a Google authorisation.
 *
 * @deprecated Kept because it is the shape three call sites already have. New
 * code should say which provider it means: {@link startOAuth}.
 */
export function startGoogle(name?: string): void {
  startOAuth("gmail", name);
}

/** How an authorisation came back. */
export type OAuthReturn = ExternalAuthReturn;

/**
 * Be told how a Google authorisation went, whenever the answer arrives.
 *
 * A subscription rather than a one-shot read, because the two builds learn this
 * at different moments. In a browser the answer is in the query string of the
 * page load that *is* the return leg, so it is there before anything else runs.
 * Through a registered URL scheme it arrives when the person finishes with
 * Google -- possibly a minute after launch, possibly twice in one run.
 *
 * The browser transport still delivers synchronously during the subscribing
 * module's initialisation, which is exactly where this has always been read, so
 * nothing about the browser's behaviour changes.
 */
export function onOAuthReturn(handler: (result: OAuthReturn) => void): void {
  onExternalAuthReturn(handler);
}
